import { GrowthBookSSRData } from '@growthbook/growthbook-react'
import { withProfiler } from '@sentry/react'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import 'focus-visible'
import { AnimateSharedLayout } from 'framer-motion'
import 'lazysizes'
import 'lazysizes/plugins/attrchange/ls.attrchange'
import { NextComponentType } from 'next'
import type { AppProps, AppContext, NextWebVitalsMetric } from 'next/app'
import dynamic from 'next/dynamic'
import Router from 'next/router'
import React from 'react'
import 'reflect-metadata'
import styled from 'styled-components'
import { NextPageWithLayout } from 'types/NextPageWithLayout'
import { PageContext } from 'types/PageContext'
import { MeQuery } from 'types/graphql'
import { getAuthIdToken, getSessionCookieToken } from 'api/authCookies'
import { setupFirebase } from 'api/firebase'
import 'utils/WhyDidYouRender'
import { useApollo, initializeApollo } from 'utils/apolloClient'
import { config } from 'utils/config'
import { getSegmentTrackingId } from 'utils/segmentCookie'
import { BodyStyles } from 'lib/GlobalStyles/Body'
import { Reset } from 'lib/GlobalStyles/Reset'
import { SwiperStyles } from 'lib/GlobalStyles/Swiper'
import { PageMeta } from 'lib/PageMeta'
import { ProgressIndicator } from 'lib/ProgressIndicator'
import { FontDefinitions } from 'lib/Text'
import { CookieNotice } from 'modules/CookieNotice'
import { UiContextProvider } from 'modules/UiContext'
import { UiImagePreloader } from 'modules/UiImages'
import { NewSessionModal } from 'modules/auth/NewSessionModal'
import { AuthenticationProvider } from 'modules/auth/context/AuthenticationProvider'
import { SessionProvider } from 'modules/auth/context/SessionProvider'
import { SimpleApolloProvider } from 'modules/auth/context/SimpleApolloProvider'
import { AlgoliaSearchProvider } from 'modules/brands/list/components/controllers/AlgoliaSearchContext'
import { CartContextProvider } from 'modules/cart/context/CartProvider'
import { CartPopover } from 'modules/checkout/CartPopover'
import { GrowthBookProvider } from 'modules/growthbook/GrowthBookProvider'
import { getInitialAttributes } from 'modules/growthbook/utils/getInitialAttributes'
import { loadFeaturesServerSide } from 'modules/growthbook/utils/loadFeaturesServerSide'
import { LevelUpModal } from 'modules/user/levels/LevelUpModal'
import { getMeQuery } from 'modules/user/queries/me'

const Onboarding = dynamic(() => import('modules/onboarding/Onboarding').then((res) => res.Onboarding), { ssr: false })

setupFirebase()

dayjs.extend(utc)
dayjs.extend(relativeTime)
dayjs.extend(duration)
dayjs.extend(advancedFormat)
dayjs.extend(timezone)

if (typeof window !== 'undefined') {
  window.analytics.load(config.segmentId)
  window.analytics.setAnonymousId(getSegmentTrackingId())
  window.analytics.page(window.location.href.replace(window.location.origin, ''))
}

Router.events.on('routeChangeComplete', (url) => {
  window.analytics.page(url)
})

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`

type AppPropsWithLayout = AppProps & {
  err: any
  Component: NextPageWithLayout<{ err: any }>
  authIdToken?: string
  sessionToken?: string
  growthBookSsrData: GrowthBookSSRData
  segmentTrackingId: string
}

const App = ({ Component, pageProps, err, authIdToken, sessionToken, growthBookSsrData, segmentTrackingId }: AppPropsWithLayout) => {
  const apolloClient = useApollo({
    initialState: (pageProps as unknown as any)?.initialApolloState,
    authorization: authIdToken,
    sessionAuthorization: sessionToken
  })
  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <SimpleApolloProvider client={apolloClient}>
      <UiContextProvider>
        <SessionProvider>
          <GrowthBookProvider ssrData={growthBookSsrData} segmentTrackingId={segmentTrackingId}>
            <AuthenticationProvider>
              <CartContextProvider>
                <PageMeta />
                <Reset />
                <FontDefinitions />
                <BodyStyles />
                <SwiperStyles />
                <UiImagePreloader />
                <ProgressIndicator />
                <AlgoliaSearchProvider>
                  <PageContainer>
                    <AnimateSharedLayout>{getLayout(<Component {...pageProps} err={err} />)}</AnimateSharedLayout>
                    <CookieNotice />
                    <NewSessionModal />
                    <LevelUpModal />
                    <CartPopover />
                    <Onboarding />
                  </PageContainer>
                </AlgoliaSearchProvider>
              </CartContextProvider>
            </AuthenticationProvider>
          </GrowthBookProvider>
        </SessionProvider>
      </UiContextProvider>
    </SimpleApolloProvider>
  )
}

/**
 * Disable static page generation so that environment variables can be set server-side
 * and reliably passed down into the JS code during SSR.
 */
App.getInitialProps = async (appContext: AppContext) => {
  const { req } = appContext.ctx
  const authIdToken = await getAuthIdToken(req)
  const sessionToken = getSessionCookieToken(req)
  const segmentTrackingId = getSegmentTrackingId(req)
  const apolloClient = initializeApollo({ req, authorization: authIdToken, sessionAuthorization: sessionToken, anonymousId: segmentTrackingId })
  let user: MeQuery['me'] | null = null

  if (sessionToken || authIdToken) {
    try {
      const { data } = await apolloClient.query<MeQuery>({ query: getMeQuery })
      user = data?.me
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Failed to fetch current user', err)
    }
  }

  const growthBookSsrData = await loadFeaturesServerSide(getInitialAttributes({ user, segmentTrackingId }))

  const Component = appContext.Component as NextComponentType<PageContext>
  let componentProps: Record<string, any> = {}

  if (Component.getInitialProps) {
    componentProps = await Component.getInitialProps({
      ...appContext.ctx,
      apolloClient,
      user
    })
  }

  return {
    authIdToken,
    sessionToken,
    growthBookSsrData,
    segmentTrackingId,
    pageProps: {
      ...componentProps,
      initialApolloState: apolloClient.cache.extract()
    }
  }
}

// https://nextjs.org/docs/advanced-features/measuring-performance
export const reportWebVitals = ({ id, name, label, value }: NextWebVitalsMetric) => {
  if (typeof window.ga !== 'undefined') {
    window.ga('send', 'event', name, {
      eventCategory: label === 'web-vital' ? 'Web Vitals' : 'Next.js custom metric',
      eventAction: name,
      eventValue: Math.round(name === 'CLS' ? value * 1000 : value), // values must be integers
      eventLabel: id, // id unique to current page load
      nonInteraction: true, // avoids affecting bounce rate.
      transport: 'beacon'
    })
  }
}

export default withProfiler(App)
